import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from "@angular/core"
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Allocations, getSuperTypeName, getTypeForSuperType, NODE_SUPERTYPE, ORGANISATION_MODEL_KEY } from "../../common/linqset-model";
import { CARBON_SCOPE, DEFAULT_CURRENCY, getCarbonScopeIcon, ModelInsights } from "../../common/organisation";
import { getCarbonScopeColour, MODEL_COLOURS } from "../../model-services/model-item-enums";
import { BarData } from "../../charts/bar-chart/bar-chart.component";
import { OrganisationService } from "../../organisation/organisation.service";

@Component({
    selector: 'app-allocations-table',
    templateUrl: './allocations-table.html',
    styleUrls: ['./allocations-table.css'],
    standalone: false
})
export class AllocationsTableComponent implements OnInit, OnChanges {
    @Input() insights:ModelInsights
    @Input() comparisonInsights:ModelInsights
    @Input() comparisonName:string
    @Input() type:string
    @Input() tableOnly:boolean
    
    columns:string[] = []
    headings:any = {}
    totals:any = {}

    barData:BarData[]
    hoursBarData:BarData[]
    stacks:string[]

    chartHeight:number
    chartWidth:number = 700

    dataSource = new MatTableDataSource<any>();
    @ViewChild(MatSort) sort: MatSort

    selectedRow:Allocations
    currencyCode:string

    constructor(organisationService:OrganisationService) {
        this.currencyCode = organisationService.organisation?.currencyCode||DEFAULT_CURRENCY
    }

    ngOnInit(): void {
        this.populateDataSource()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['insights'] || changes['comparisonInsights'] || changes['allocatedOnly']) this.populateDataSource()
    }

    selectRow(row:Allocations) {
        this.selectedRow = row
    }

    getSuperType(superType:NODE_SUPERTYPE) {
        return getSuperTypeName(superType)
    }

    getCarbonIcon(row:Allocations) {
        return getCarbonScopeIcon(row.scope)
    }

    setupHeadings() {
        this.headings = {}
        this.headings['icon'] = "Icon"
        this.headings['scope'] = "Scope"
        this.headings['superType'] = "Type"
        this.headings['colour'] = "Legend"
        this.headings['hours'] = "Hours"
        this.headings['allocated'] = "Allocated"
        this.headings['unallocated'] = "Unallocated"
        this.headings['unmodelled'] = "Unmodelled"
    }

    isCost() {
        return this.type==="cost"
    }

    populateDataSource() {
        const allocations = this.isCost()?this.insights.costAllocations:this.insights.emissions
        const comparison = this.comparisonInsights && (this.isCost()?this.comparisonInsights.costAllocations:this.comparisonInsights.emissions)

        this.chartHeight = allocations.length*175

        if (allocations && allocations.length > 0) {

            this.setupHeadings()

            this.columns = this.isCost()?['icon', 'superType']:['icon', 'scope']
            
            if (!this.tableOnly) this.columns.push('colour')

            let rows = []
            this.barData = []
            this.hoursBarData = []

            this.stacks = allocations.map(a => {
                return a.superType?this.getSuperType(a.superType):a.scope
            })
       
            if (comparison && comparison.length > 0) {
                if (this.isCost()) this.columns.push("hours2", "hours")
                this.columns = this.columns.concat(["allocated2", "allocated"])
                if (this.insights.modelKey === ORGANISATION_MODEL_KEY) this.columns = this.columns.concat(["unallocated2", "unallocated", "unmodelled2", "unmodelled"])    
            }else {
                if (this.isCost()) this.columns.push("hours")
                this.columns.push("allocated")
                if (this.insights.modelKey === ORGANISATION_MODEL_KEY) {
                    this.columns.push("unallocated")
                    this.columns.push("unmodelled")
                }
            }

            rows = allocations.map(a => {
                let compareRow = {}
                if (comparison && comparison.length > 0) {
                    const compare = comparison.find(c => c.scope === a.scope && c.superType === a.superType)
                    const hours2 = a.superType && this.comparisonInsights.superTypes[a.superType]?.hours||0
                    compareRow = {allocated2:compare?.allocated||0, unallocated2:compare?.unallocated||0, unmodelled2:compare?.unmodelled||0, hours2}
                }
                const hours = a.superType && this.insights.superTypes[a.superType]?.hours||0
                const row = {...a, hours, type:getTypeForSuperType(a.superType), ...compareRow}
                return row
            })

            this.dataSource.data = rows

            this.totals = {}
            this.columns.filter(column => (column !== 'icon' && column !== 'scope' && column !== 'superType' && column !== 'colour')).forEach(column => {
                const bd = this.headings[column] && new BarData(column, this.headings[column], [], [], [], [])
                if (bd) {
                    if (column === "hours" || column === "hours2")
                        this.hoursBarData.push(bd)
                    else this.barData.push(bd)
                }
                rows.forEach(row => {
                    if (!this.totals[column]) this.totals[column] = 0
                    this.totals[column] += row[column]

                    if (bd) {
                        bd.values.push(row[column])
                        bd.colors.push(this.getColour(row.scope, row.superType))
                        if (comparison) bd.csValues.push(row[column + "2"])
                    }
                })
            })   

        }
    }

    getHeaderStyle(column:string) {
        if (this.insights.modelKey !== ORGANISATION_MODEL_KEY) return ""
        if (column.startsWith("allocated") || column.startsWith('unmodelled'))
            return "background:rgba(0, 169, 165, 0.05);"

        return ""
    }

    getNumberStyle(row:any, column:string) {
        const background = this.getHeaderStyle(column)

        if (this.columns.indexOf(column + '2') < 0) return background

        const before = row[column + "2"]
        const after = row[column]

        if (after > before) return "color:#15803d;font-weight:600;" + background
        else if (before > after) return "color:#b42318;font-weight:600;" + background
        else return "color:#9aa0b0;" + background
    }

    getHeadings() {
        return this.columns?this.columns.filter(c => !c.endsWith('2')):[]
    }

    getHeadingWidth(column:string) {
        return this.comparisonInsights && column !== 'icon' && column !== 'scope' && column !== 'superType'  && column !== 'colour'?2:1
    }

    getColour(scope:CARBON_SCOPE, superType:NODE_SUPERTYPE) {
        if (scope) {
            return getCarbonScopeColour(scope)
        }else {
            switch (superType) {
                case NODE_SUPERTYPE.INFRASTRUCTURE:
                    return MODEL_COLOURS.INFRASTRUCTURE_COLOR
                case NODE_SUPERTYPE.PEOPLE:
                    return MODEL_COLOURS.PEOPLE_COLOR
                case NODE_SUPERTYPE.ROLE:
                    return MODEL_COLOURS.ROLE_COLOR
                case NODE_SUPERTYPE.SYSTEM:
                    return MODEL_COLOURS.SYSTEM_COLOR
            }
        }
        return undefined
    }
}
